/**
  * Share widget
  * - Works for every instance on the page: PDP, product cards (including ones loaded
  *   later via AJAX/infinite scroll/filtering), and the quick preview modal.
  * - Custom popover with a fixed channel list: Copy Link, Email, WhatsApp, Messenger,
  *   Facebook, Twitter/X, Print, and a "More Options" button that opens the native
  *   OS share sheet where supported (navigator.share).
  * - Appends UTM tracking params to shared URLs so returning clicks are attributable in GA4.
  * - GA4 event tracking hook included, left inactive pending confirmation from the client.
*/

(function () {
  function withTrackingParams(url, channel, context) {
    try {
      const separator = url.includes('?') ? '&' : '?';
      const params = new URLSearchParams({
        utm_source: 'share',
        utm_medium: channel,
        utm_campaign: 'product_share',
        utm_content: context || 'pdp',
      });
      return `${url}${separator}${params.toString()}`;
    } catch (e) {
      return url;
    }
  }

  function buildShareLink({ url, title, channel, context }) {
    const trackedUrl = withTrackingParams(url, channel, context);
    const encodedUrl = encodeURIComponent(trackedUrl);
    const encodedTitle = encodeURIComponent(title || '');
    const links = {
      whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`,
      email: `mailto:?subject=${encodedTitle}&body=${encodedTitle}%0A%0A${encodedUrl}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      twitter: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
      messenger: `https://www.facebook.com/dialog/send?link=${encodedUrl}&app_id=&redirect_uri=${encodedUrl}`,
    };
    return { url: trackedUrl, link: links[channel] };
  }

  function showToast(widget, message) {
    let toast = widget.querySelector('.share-widget__toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.className = 'share-widget__toast';
      widget.appendChild(toast);
    }
    toast.textContent = message;
    requestAnimationFrame(() => toast.classList.add('share-widget__toast--visible'));
    setTimeout(() => toast.classList.remove('share-widget__toast--visible'), 1800);
  }

  // ── Positioning: computed against the viewport, immune to ancestor transforms ──
  function positionPopover(popover, trigger) {
    const margin = 8;
    const viewportWidth = window.innerWidth;
    const viewportHeight = window.innerHeight;

    popover.style.position = 'fixed';
    popover.style.top = '0px';
    popover.style.left = '0px';
    popover.style.right = 'auto';
    popover.style.bottom = 'auto';
    popover.style.transform = 'none';

    const triggerRect = trigger.getBoundingClientRect();
    const width = popover.offsetWidth;
    const height = popover.offsetHeight;

    let top = triggerRect.bottom + margin;
    let left = triggerRect.right - width;

    if (top + height > viewportHeight - margin) {
      top = triggerRect.top - height - margin;
    }
    if (left < margin) left = margin;
    if (left + width > viewportWidth - margin) left = viewportWidth - width - margin;
    if (top < margin) top = margin;

    popover.style.top = `${top}px`;
    popover.style.left = `${left}px`;
  }

  function positionTooltip(tooltip, trigger) {
    const margin = 8;
    const viewportWidth = window.innerWidth;

    tooltip.style.position = 'fixed';
    tooltip.style.left = '0px';
    tooltip.style.top = '0px';
    tooltip.style.transform = 'none';

    const triggerRect = trigger.getBoundingClientRect();
    const width = tooltip.offsetWidth;

    let left = triggerRect.left + triggerRect.width / 2 - width / 2;
    let top = triggerRect.top - tooltip.offsetHeight - margin;

    if (left < margin) left = margin;
    if (left + width > viewportWidth - margin) left = viewportWidth - width - margin;

    tooltip.style.left = `${left}px`;
    tooltip.style.top = `${top}px`;
  }

  function closePopover(popover, trigger) {
    if (!popover || !trigger) return;
    popover.hidden = true;
    trigger.setAttribute('aria-expanded', 'false');
  }

  function openPopover(popover, trigger) {
    const hostDialog = trigger.closest('dialog[open]');
    const target = hostDialog || document.body;

    if (popover.parentElement !== target) {
      target.appendChild(popover);
    }
    popover.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');
    positionPopover(popover, trigger);
  }

  function closeAllPopovers() {
    document.querySelectorAll('[data-share-popover]:not([hidden])').forEach((p) => {
      const trigger = p._shareTrigger;
      closePopover(p, trigger);
    });
  }

  function trackShare(context, channel) {
    // TRACKING (inactive until confirmed):
    // if (window.gtag) {
    //   gtag('event', 'share', {
    //     method: channel,
    //     content_type: 'product',
    //     page_type: context,
    //   });
    // }
  }

  function initWidget(widget) {
    if (widget.dataset.shareInitialized) return;
    widget.dataset.shareInitialized = 'true';

    const trigger = widget.querySelector('[data-share-trigger]');
    const popover = widget.querySelector('[data-share-popover]');
    const tooltip = widget.querySelector('[data-share-trigger] .share-widget__tooltip');
    if (!trigger || !popover) return;

    // Store the trigger reference directly on the popover — once it's
    // portaled to <body>, parentElement lookups no longer work.
    popover._shareTrigger = trigger;

    // ── Tooltip: portal + position on show, same escape-the-clip trick as the popover ──
    if (tooltip) {
      const showTooltip = () => {
        const target = trigger.closest('dialog[open]') || document.body;
        if (tooltip.parentElement !== target) {
          target.appendChild(tooltip);
        }
        positionTooltip(tooltip, trigger);
        tooltip.classList.add('share-widget__tooltip--visible');
      };
      const hideTooltip = () => {
        tooltip.classList.remove('share-widget__tooltip--visible');
      };
    
      trigger.addEventListener('mouseenter', showTooltip);
      trigger.addEventListener('mouseleave', hideTooltip);
      trigger.addEventListener('focus', showTooltip);
      trigger.addEventListener('blur', hideTooltip);
    }

    const context = widget.getAttribute('data-context') || 'pdp';

    trigger.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();

      const isOpen = !popover.hidden;
      closeAllPopovers();
      if (!isOpen) openPopover(popover, trigger);
    });

    popover.addEventListener('click', async (e) => {
      const item = e.target.closest('[data-share-channel]');
      if (!item) return;
      e.preventDefault();
      e.stopPropagation();
      const channel = item.getAttribute('data-share-channel');

      const url = trigger.getAttribute('data-share-url');
      const title = trigger.getAttribute('data-share-title');

      if (channel === 'copy_link') {
        const { url: trackedUrl } = buildShareLink({ url, title, channel, context });
        try {
          await navigator.clipboard.writeText(trackedUrl);
          const tooltip = item.querySelector('[data-copy-tooltip]');
          if (tooltip) {
            tooltip.classList.add('share-widget__copy-tooltip--visible');
            setTimeout(() => {
              tooltip.classList.remove('share-widget__copy-tooltip--visible');
              closePopover(popover, trigger);
            }, 1200);
          } else {
            closePopover(popover, trigger);
          }
        } catch (err) {
          closePopover(popover, trigger);
        }
        trackShare(context, 'copy_link');
        return;
      } else if (channel === 'print') {
        trackShare(context, 'print');
        window.print();
      } else if (channel === 'more') {
        if (navigator.share) {
          try {
            const { url: trackedUrl } = buildShareLink({ url, title, channel: 'native', context });
            await navigator.share({ title, url: trackedUrl });
            trackShare(context, 'native');
          } catch (err) {}
        }
      } else {
        const { link } = buildShareLink({ url, title, channel, context });
        if (link) {
          window.open(link, '_blank', 'noopener,noreferrer,width=600,height=500');
          trackShare(context, channel);
        }
      }

      closePopover(popover, trigger);
    });
  }

  function scanAndInit(root) {
    (root || document).querySelectorAll('[data-share-widget]').forEach(initWidget);
  }

  document.addEventListener('DOMContentLoaded', () => scanAndInit(document));

  // Re-scan when new markup is injected — covers AJAX-loaded product cards
  const observer = new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches('[data-share-widget]')) {
          initWidget(node);
        } else if (node.querySelectorAll) {
          scanAndInit(node);
        }
      });
    }
  });
  observer.observe(document.body, { childList: true, subtree: true });

  // Close any open popover on outside click / Escape
  document.addEventListener('click', closeAllPopovers);
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') closeAllPopovers();
  });

  window.addEventListener(
    'scroll',
    () => {
      document.querySelectorAll('[data-share-popover]:not([hidden])').forEach((p) => {
        if (p._shareTrigger) positionPopover(p, p._shareTrigger);
      });
    },
    { passive: true, capture: true }
  );

  window.addEventListener('resize', () => {
    document.querySelectorAll('[data-share-popover]:not([hidden])').forEach((p) => {
      if (p._shareTrigger) positionPopover(p, p._shareTrigger);
    });
  });

  // Expose for manual re-init if a theme needs to call it explicitly
  window.ShareWidget = { init: scanAndInit };
})();